import React, { SetStateAction, useEffect, useState } from "react";
import iconClose from '../../images/icon-close.svg';
import { Controller, useForm } from 'react-hook-form'
import TextInput from '../common/input/textInput/TextInput'
import TextArea from '../common/input/textArea/TextArea'
import styles from './Roles.module.css'
import { createNewRole, updateRole } from "../../redux/roles/actions/roles.action";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/rootReducer";
import { RoleProps, ToastType } from "./Roles";
import Button from "../common/button/Button";
import Loader from "../common/loader/Loader";

interface RoleModalProps {
    selectedId: RoleProps | null;
    setShowModal: React.Dispatch<SetStateAction<boolean>>
    setSelectedId: React.Dispatch<SetStateAction<RoleProps | null>>
    setShowToast: React.Dispatch<SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<SetStateAction<string | null>>;
    setToastType: React.Dispatch<SetStateAction<ToastType | null>>;
    onSuccess: () => void;
}

export const RoleModalContent = ({ selectedId, setShowModal, setSelectedId, setShowToast, setToastMessage, setToastType, onSuccess }: RoleModalProps) => {
    const [initialValues, setInitialValues] = useState({ roleName: "", description: "" });
    const [roleLoading, setRoleLoading] = useState(false);
    const dispatch: AppDispatch = useDispatch();

    const {
        handleSubmit,
        setValue,
        formState: { isValid, errors },
        control,
        reset,
        watch
    } = useForm({
        defaultValues: {
            roleName: "",
            description: ""
        },
        mode: "onChange",
    });


    const watchedValues = watch(); // Watch all form fields

    useEffect(() => {
        if (selectedId) {
            setValue("description", selectedId?.description);
            setValue("roleName", selectedId?.name)
            const fetchedData = { roleName: selectedId.name, description: selectedId.description };
            setInitialValues(fetchedData);
        }
    }, [selectedId]);

    const isFormChanged = JSON.stringify(initialValues) !== JSON.stringify(watchedValues);

    const onCloseModal = () => {
        reset();
        setShowModal(false);
    }

    const onSubmit = (data: { roleName: string, description: string }) => {
        setRoleLoading(true)
        if (selectedId) {
            dispatch(updateRole(selectedId?.id, data?.roleName, data?.description, onNewRoleSuccess, onNewRoleFailure))
        } else {
            dispatch(createNewRole(data?.roleName, data?.description, onNewRoleSuccess, onNewRoleFailure))
        }
    }

    const onNewRoleSuccess = (message: string) => {
        setSelectedId(null);
        setRoleLoading(false);
        setToastType('success');
        setShowToast(true);
        setToastMessage(message);
        onSuccess()
        setShowModal(false);
        reset();
    }

    const onNewRoleFailure = (message: string) => {
        setRoleLoading(false)
        setToastType('error');
        setShowToast(true);
        setToastMessage(message)
    }

    return (
        <>
            {roleLoading && <Loader />}
            <div className={styles.modalHeaderContainer}>
                <h2 className={styles.modalHeader}>{selectedId ? `Edit Role` : 'Add Role'}</h2>
                <div className={`${styles.close} ${roleLoading ? styles.disabled : ''}`}>
                    <img src={iconClose} onClick={() => (!roleLoading ? onCloseModal() : null)} alt="Close" />
                </div>
            </div>

            <div className={styles.modalBodyContainer}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        name="roleName"
                        control={control}
                        rules={{
                            required: "Role Name is required"
                        }}
                        render={({ field }) => {
                            return (<div className={styles.fieldWrapper}>
                                <TextInput
                                    {...field}
                                    name='roleName'
                                    id='roleName'
                                    placeholder='Enter Role Name'
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                        }
                                    }}
                                    error={errors?.roleName && errors?.roleName?.message}
                                />
                            </div>)
                        }}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                            return (<div className={styles.fieldWrapper}>
                                <TextArea
                                    name='description'
                                    id='description'
                                    placeholder='Enter Role Description'
                                    onChange={onChange}
                                    value={value}
                                    maxLength={150}
                                    aria-rowcount={2}
                                />
                            </div>)
                        }}
                    />

                    <div className={styles.modalFooter}>
                        <Button onClick={onCloseModal} style={{ minWidth: 100 }} variant='outlined-green' disabled={roleLoading} >Cancel</Button>
                        <Button type='submit' variant='green' style={{ minWidth: 100 }} disabled={(roleLoading || !isValid || !isFormChanged)} >{selectedId ? 'Update' : 'Add'}</Button>
                    </div>
                </form>
            </div>
        </>
    );
};